import BackspaceIcon from "@mui/icons-material/Backspace";
import { DateTime } from "luxon";
import React, { useCallback, useEffect, useState } from "react";
import { IntlShape, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useRecoilValue } from "recoil";
import { useGetStatusList } from "../../Pricing/utils";
import { granularityAtom } from "../AlertGranularityTabs";

import { StyledChip } from "./Chips/StyleChip";
import { MODE } from "./CreateFilterDateForm";
import { useFilterServices } from "./CreateFilterForm";
import {
  FilterComparison,
  FilterDate,
  FilterDateRange,
  FilterDimension,
  FilterPickup,
  FilterStatus,
  FilterType,
} from "./FilterType";
import { DimensionOptions, Filter, FilterOptions } from "../../services/Alert";
import { Dimension } from "../../services/Category";

export const getMultipleLabel = (
  label: string,
  filterOptions: FilterOptions
) => {
  const options = filterOptions as DimensionOptions;
  return `${options.values.length} ${label}`;
};

export const FilterChip = ({
  filter,
  onClick,
  onDelete,
  moduleSlug,
}: {
  filter: FilterDimension;
  onClick: (data: FilterDimension) => void;
  onDelete: (data: FilterDimension) => void;
  moduleSlug: string;
}) => {
  const intl = useIntl();
  // TODO: cache this ?
  const granularityValue = useRecoilValue(granularityAtom);
  const { getDimension, cacheNamespace, translateDimensionLabels } =
    useFilterServices();
  const query = useQuery([cacheNamespace, "dimension", filter.target], () =>
    getDimension(filter.target as number)
  );
  const [label, setLabel] = useState("");

  const setModel = useCallback(
    (filter: FilterDimension, dimension: Dimension) => {
      const label = translateDimensionLabels
        ? intl.formatMessage({ id: dimension.label })
        : dimension.label;
      if (filter.type === FilterType.MULTIPLE) {
        setLabel(getMultipleLabel(label, filter.options));
      } else {
        setLabel(label);
      }
    },
    [intl, translateDimensionLabels]
  );

  useEffect(() => {
    if (query.data) {
      setModel(filter, query.data);
    }
  }, [filter, query.data, setModel]);

  return (
    <>
      <StyledChip
        key={filter.target}
        color="secondary"
        disabled={
          moduleSlug === "cockpit"
            ? false
            : query.data && query.data.granularity > granularityValue
        }
        label={label}
        onClick={() => onClick(filter)}
        onDelete={() => onDelete(filter)}
        deleteIcon={<BackspaceIcon />}
      />
    </>
  );
};

const getChipDateLabel = (filter: FilterDate, intl: IntlShape) => {
  if (filter.options.type === "custom") {
    const options = filter.options as unknown as {
      start: DateTime;
      end: DateTime;
    };
    return `Date: ${options.start.toLocaleString()} - ${options.end.toLocaleString()}`;
  }
  if (filter.options.type === MODE.LEAD_TIME) {
    return `Date: ${intl.formatMessage(
      { id: "ROLLING_LEAD_TIME" },
      { days: filter.options.days }
    )}`;
  }
  return `Date: ${intl.formatMessage({ id: filter.options.type })}`;
};

/** Booking-date bounds of the pickup filter, in the same shape as the date
 *  chip. The options carry ISO strings, hence the reparse. */
const getChipPickupLabel = (filter: FilterPickup, intl: IntlShape) => {
  const { date_reservation_start, date_reservation_end } = filter.options;
  const format = (date: string) => DateTime.fromISO(date).toLocaleString();

  if (date_reservation_start && date_reservation_end) {
    return `Pickup: ${format(date_reservation_start)} - ${format(
      date_reservation_end
    )}`;
  }
  if (date_reservation_start)
    return `Pickup: > ${format(date_reservation_start)}`;
  if (date_reservation_end) return `Pickup: < ${format(date_reservation_end)}`;
  return `Pickup: ${intl.formatMessage({ id: "TOTAL" })}`;
};

export const PickupChip = ({
  filter,
  onClick,
}: {
  filter: FilterPickup;
  onClick: () => void;
}) => {
  const intl = useIntl();

  return (
    <StyledChip
      key="date"
      color="secondary"
      label={getChipPickupLabel(filter, intl)}
      onClick={() => onClick()}
    />
  );
};

export const ComparisonChip = ({
  filter,
  onClick,
}: {
  filter: FilterComparison;
  onClick: () => void;
}) => {
  const intl = useIntl();
  const date = filter.options.comparison_date;

  return (
    <StyledChip
      key="comparison"
      color="secondary"
      label={`${intl.formatMessage({ id: "COMPARISON_DATE" })}: ${
        date ? DateTime.fromISO(date).toLocaleString() : "-"
      }`}
      onClick={() => onClick()}
    />
  );
};

export const DateChip = ({
  filter,
  onClick,
}: {
  filter: FilterDate;
  onClick: () => void;
}) => {
  const intl = useIntl();
  const [label, setLabel] = useState("");

  const setModel = useCallback(
    (filter: any) => {
      const label = getChipDateLabel(filter, intl);
      setLabel(label);
    },
    [intl]
  );

  useEffect(() => {
    setModel(filter);
  }, [filter, setModel]);

  return (
    <>
      <StyledChip
        key="date"
        color="secondary"
        label={label}
        onClick={() => onClick()}
      />
    </>
  );
};

export const DateRangeChip = ({
  filter,
  onClick,
  onDelete,
}: {
  filter: FilterDateRange;
  onClick: () => void;
  onDelete: (filter: FilterDateRange) => void;
}) => {
  const intl = useIntl();

  const getLabel = useCallback(() => {
    let startDate = "";
    let endDate = "";

    // We add one hour to be ok with date timezone od backend data
    // This will be to remove when backend will be fix
    if (filter.options.values[0]) {
      let currDate = filter.options.values[0].split(" ");
      if (currDate[1]) {
        let ISODate = currDate.join("T");
        startDate = DateTime.fromISO(ISODate)
          .plus({ hours: 1 })
          .toFormat("yyyy-MM-dd HH:mm");
      } else {
        startDate = filter.options.values[0];
      }
    }

    if (filter.options.values[1]) {
      let currDate = filter.options.values[1].split(" ");
      if (currDate[1]) {
        let ISODate = currDate.join("T");
        endDate = DateTime.fromISO(ISODate)
          .plus({ hours: 1 })
          .toFormat("yyyy-MM-dd HH:mm");
      } else {
        endDate = filter.options.values[1];
      }
    }

    if (!startDate || !endDate) {
      return `${intl.formatMessage({
        id: "UPDATED_AT",
      })}: ${startDate || endDate} ${startDate ? ">" : "<"}`;
    }

    return `${intl.formatMessage({
      id: "UPDATED_AT",
    })}: ${startDate} - ${endDate}`;
  }, [filter.options.values, intl]);

  return (
    <>
      <StyledChip
        key="date"
        color="secondary"
        label={getLabel()}
        onClick={() => onClick()}
        onDelete={() => onDelete(filter)}
        deleteIcon={<BackspaceIcon />}
      />
    </>
  );
};

export const StatusChip = ({
  filter,
  onClick,
}: {
  filter: FilterStatus;
  onClick: () => void;
}) => {
  const statuses = useGetStatusList();
  const [label, setLabel] = useState("");

  const setModel = useCallback(
    (filter: any) => {
      const label = `Statut: ${
        statuses.find((s) => s.name === filter.options.value)?.label ?? ""
      }`;
      setLabel(label);
    },
    [statuses]
  );

  useEffect(() => {
    setModel(filter);
  }, [filter, setModel]);

  return (
    <>
      <StyledChip
        key="status"
        color="secondary"
        label={label}
        onClick={() => onClick()}
      />
    </>
  );
};

export const FilterList = ({
  children,
  filters,
}: {
  children?:
    | ((filter: Filter, index: number) => React.ReactNode)
    | React.ReactNode;
  filters: Filter[];
}) => {
  return (
    <>
      {filters.map(
        (filter: Filter, index) =>
          typeof children == "function" && children(filter, index)
      )}
    </>
  );
};
